// selectionSpan — pure helpers behind the select→annotate gesture
// (.claude/analysis/pdf-source-view-sota.md §3.1). Two jobs, kept apart from
// `AnnotatedBlock.tsx`'s DOM/port wiring so the actual span-building
// math is unit-testable without a browser:
//
//   - `spanFromSelection` turns "a block's full text + a selected
//     sub-range within it" into a `SelectedSpan` (the write side — what
//     `placeHighlight`/`placeAnnotation` anchor to).
//   - `markRuns` turns "a block's full text + the quotes already placed
//     on it" into renderable runs (the read side — inline tint).
//
// CONTEXT_CHARS bounds how much surrounding text `prefix`/`suffix` carry
// — enough for `Core.Anchor`'s quote re-find to disambiguate a repeated
// phrase, cheap enough to never bloat the wire payload.

export type { MarkRun };
export { markRuns, spanFromSelection };

import type { SelectedSpan } from "./types.js";

const CONTEXT_CHARS = 24;

// A validated, non-empty selection within `blockText` → its `SelectedSpan`.
// `undefined` when the selection is empty/whitespace-only or out of
// bounds — the caller (a DOM Range read) is responsible for start<=end
// and both within [0, blockText.length]; this function re-validates
// rather than trusting the caller, since it is the ONE place a malformed
// browser Range could otherwise reach the port layer.
const spanFromSelection = (
    blockText: string,
    start: number,
    end: number,
): SelectedSpan | undefined => {
    if (
        !Number.isInteger(start) ||
        !Number.isInteger(end) ||
        start < 0 ||
        end > blockText.length ||
        start >= end
    ) {
        return undefined;
    }
    const exact = blockText.slice(start, end);
    if (exact.trim().length === 0) return undefined;
    const prefix = blockText.slice(Math.max(0, start - CONTEXT_CHARS), start);
    const suffix = blockText.slice(end, end + CONTEXT_CHARS);
    return {
        exact,
        start,
        end,
        ...(prefix.length > 0 ? { prefix } : {}),
        ...(suffix.length > 0 ? { suffix } : {}),
    };
};

// One run of a block's text: plain prose, or tinted because it's the
// FIRST occurrence of one of `quotes` (later occurrences of the same
// string are NOT re-tinted — a simple, honest first-match rule; the
// robust drift-aware re-find `Core.Anchor.resolveSelector` performs is a
// heavier operation this render-time splitter doesn't reproduce, per
// `SelectedSpan`'s own header note). Longer quotes win when two overlap
// at the same start (checked longest-first) so a nested/prefix quote
// never fragments a longer one.
type MarkRun =
    | { readonly type: "plain"; readonly text: string }
    | {
          readonly type: "marked";
          readonly text: string;
          readonly quoteIndex: number;
      };

const markRuns = (
    blockText: string,
    quotes: readonly string[],
): readonly MarkRun[] => {
    const nonEmpty = quotes
        .map((q, index) => ({ q, index }))
        .filter(({ q }) => q.length > 0)
        .sort((a, b) => b.q.length - a.q.length);
    if (nonEmpty.length === 0) return [{ type: "plain", text: blockText }];

    // Each quote is consumed after its first match — `remaining` shrinks as
    // the scan proceeds, so a quote string repeated elsewhere in the block
    // (e.g. "cat cat cat" with one "cat" annotation) tints only the
    // occurrence this pass finds, never every occurrence.
    let remaining = nonEmpty;
    const runs: MarkRun[] = [];
    let cursor = 0;
    while (cursor < blockText.length) {
        const hit = remaining
            .map(({ q, index }) => ({
                at: blockText.indexOf(q, cursor),
                q,
                index,
            }))
            .filter(({ at }) => at !== -1)
            .sort((a, b) => a.at - b.at || b.q.length - a.q.length)[0];
        if (hit === undefined) {
            runs.push({ type: "plain", text: blockText.slice(cursor) });
            break;
        }
        if (hit.at > cursor) {
            runs.push({ type: "plain", text: blockText.slice(cursor, hit.at) });
        }
        runs.push({ type: "marked", text: hit.q, quoteIndex: hit.index });
        cursor = hit.at + hit.q.length;
        remaining = remaining.filter(({ index }) => index !== hit.index);
    }
    return runs;
};
